import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import type { Settings } from 'react-slick';
import Slider from 'react-slick';
import type { GalleryBlockTranslations } from '../../../ViewModel/GalleryBlockTranslations.csharp';
import type { GalleryColorVariant } from '../../GalleryColorVariant';
import type { GalleryDesktopHeightVariant } from '../../GalleryDesktopHeightVariant';
import type { GalleryMobileHeightVariant } from '../../GalleryMobileHeightVariant';
import { GalleryItem } from '../GalleryItem/GalleryItem';
import type { GalleryItemProps } from '../GalleryItem/GalleryItem';
import { Button } from '~/Partials/Button/Button';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { MediaBreakpoints } from '~/Utils/MediaBreakpoints';
import styles from './GallerySection.module.css';

export interface GallerySectionItemProps extends Pick<GalleryItemProps, 'image' | 'ratio'> {
    caption?: string;
}

interface GallerySectionProps {
    galleryItems: GallerySectionItemProps[];
    translations: GalleryBlockTranslations;
    desktopHeightVariant: GalleryDesktopHeightVariant;
    mobileHeightVariant: GalleryMobileHeightVariant;
    colorVariant: GalleryColorVariant;
}

const renderArrow = (
    buttonLabel: string,
    iconName: 'arrow-left' | 'arrow-right',
    onClick: () => void,
    disabled: boolean
) => (
    <Button
        type={ButtonType.Clear}
        aria-label={buttonLabel}
        className={styles.Arrow}
        onClick={onClick}
        disabled={disabled}
    >
        <SvgIcon iconName={iconName} className={styles.ArrowIcon} />
    </Button>
);

export const GallerySection: React.FC<GallerySectionProps> = props => {
    const { galleryItems, translations, desktopHeightVariant, mobileHeightVariant, colorVariant } = props;

    const [currentSlide, setCurrentSlide] = React.useState(0);
    const sliderRef = React.useRef<Slider | null>(null);

    const sliderSettings: Settings = {
        arrows: false,
        infinite: false,
        slidesToShow: 1,
        variableWidth: true,
        beforeChange: (_, nextIndex) => setCurrentSlide(nextIndex),
        afterChange: current => setCurrentSlide(current),
        responsive: [
            {
                breakpoint: MediaBreakpoints.Tablet,
                settings: {
                    centerMode: true,
                    centerPadding: '0px',
                },
            },
        ],
    };

    const isPrevDisabled = currentSlide === 0;
    const isNextDisabled = currentSlide === galleryItems.length - 1;

    return (
        <div className={classNames(styles.GallerySection, styles[`Variant--${colorVariant}`])}>
            <div className={styles.Wrapper}>
                <Slider ref={sliderRef} {...sliderSettings}>
                    {galleryItems.map(item => (
                        <GalleryItem
                            key={uniqueId('GalleryItem__')}
                            {...item}
                            colorVariant={colorVariant}
                            desktopHeightVariant={desktopHeightVariant}
                            mobileHeightVariant={mobileHeightVariant}
                        />
                    ))}
                </Slider>
                <div className={styles.Arrows}>
                    {renderArrow(
                        translations.previousImage,
                        'arrow-left',
                        () => sliderRef.current?.slickPrev(),
                        isPrevDisabled
                    )}
                    {renderArrow(
                        translations.nextImage,
                        'arrow-right',
                        () => sliderRef.current?.slickNext(),
                        isNextDisabled
                    )}
                </div>
            </div>
        </div>
    );
};
